import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sailboat, CircleDot, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import kayakImg from "@/assets/kayaking.jpg";
import paddleImg from "@/assets/blog-hero-paddleboard.jpg";
import floatImg from "@/assets/cabana-floating-mat.jpg";

interface Rental {
  icon: typeof Sailboat;
  title: string;
  description: string;
  image: string;
}

const RENTALS: Rental[] = [
  {
    icon: Sailboat,
    title: "Kayaks",
    description: "Single & tandem sit-on-tops — paddle the calm Lake Placid perimeter.",
    image: kayakImg,
  },
  {
    icon: CircleDot,
    title: "Stand-Up Paddleboards",
    description: "10'–11' all-around SUPs — flat, spring-fed water, great for beginners.",
    image: paddleImg,
  },
  {
    icon: LifeBuoy,
    title: "iFloats",
    description: "Big floating lily-pad mats — lounge, jump, repeat.",
    image: floatImg,
  },
];

interface Props {
  eyebrow?: string;
  heading?: string;
  subhead?: string;
  ctaHref?: string;
  ctaLabel?: string;
  id?: string;
}

const WatercraftRentalsSection = ({
  eyebrow = "Optional Add-On",
  heading = "Add Kayaks, Paddleboards & iFloats",
  subhead = "Our most-loved upgrade. One wristband, unlimited 45-minute sessions all day — life jackets included.",
  ctaHref = "/book-now",
  ctaLabel = "Add to Your Booking",
  id,
}: Props) => {
  return (
    <section id={id} style={id ? { scrollMarginTop: "96px" } : undefined} className="py-20 lg:py-24 bg-background">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1 bg-accent/10 text-accent rounded-full text-sm font-semibold mb-4">
            {eyebrow}
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            {heading}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{subhead}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {RENTALS.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300 border border-border"
            >
              <div className="relative">
                <img
                  src={r.image}
                  alt={r.title}
                  loading="lazy"
                  width={800}
                  height={600}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute bottom-2 right-2 w-11 h-11 rounded-xl bg-background/90 backdrop-blur flex items-center justify-center shadow-soft">
                  <r.icon className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="p-5 text-center">
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {r.title}
                </h3>
                <p className="text-sm text-muted-foreground">{r.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 flex flex-col items-center gap-4"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-6 bg-accent/10 rounded-2xl px-6 py-5 text-center">
            <div>
              <p className="text-3xl font-bold text-accent leading-none">$29.99</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">per person</p>
            </div>
            <div className="text-sm text-foreground sm:text-left">
              <p className="font-semibold">All-day watercraft wristband</p>
              <p className="text-muted-foreground">
                Unlimited 45-minute sessions · life jackets provided free
              </p>
            </div>
          </div>
          <Button asChild variant="hero" size="lg">
            <Link to={ctaHref}>{ctaLabel}</Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default WatercraftRentalsSection;